import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { metricsService } from '@/services/metricsService';
import { toast } from 'sonner';
import type { SummaryPitch, VideoPitch } from '@/types/pitch';

export interface ViewsSeries {
  date: string;
  views_total: number;
  views_unique: number;
  plays_total: number;
  plays_unique: number;
  play_rate: number;
}

export interface RetentionTimeline {
  time_seconds: number;
  viewers_count: number;
  retention_pct: number;
}

export interface GeoData {
  pais?: string;
  estado?: string;
  cidade?: string;
  total: number;
  percentual: string;
  views: number;
  uniqueViews: number;
  plays: number;
  uniquePlays: number;
  engajamento: string;
  /** Retenção (%) em cada pitch, na ordem de `video.pitches`. null = sem dado. */
  pitchesRetentionPct?: Array<number | null>;
}

export interface DeviceData {
  plataforma?: string;
  browser?: string;
  total: number;
  percentual: string;
  views: number;
  uniqueViews: number;
  plays: number;
  uniquePlays: number;
  engajamento: string;
  /** Retenção (%) em cada pitch, na ordem de `video.pitches`. null = sem dado. */
  pitchesRetentionPct?: Array<number | null>;
}

export interface ReferenceData {
  page_origin?: string;
  total: number;
  percentual: string;
  views: number;
  uniqueViews: number;
  plays: number;
  uniquePlays: number;
  engajamento: string;
  /** Retenção (%) em cada pitch, na ordem de `video.pitches`. null = sem dado. */
  pitchesRetentionPct?: Array<number | null>;
}

export interface TrafficSourceItem {
  value: string;
  views: number;
  uniqueViews: number;
  plays: number;
  uniquePlays: number;
  clicks: number;
  engagement: number;
  avgRetention: number;
  /** Retenção (%) e plays que chegaram em cada pitch, na ordem de `video.pitches`. */
  pitchesRetentionPct?: Array<number | null>;
  pitchesViewsCount?: Array<number | null>;
}

export interface TrafficSourcesByType {
  utm_source: TrafficSourceItem[];
  utm_medium: TrafficSourceItem[];
  utm_campaign: TrafficSourceItem[];
  utm_term: TrafficSourceItem[];
  utm_content: TrafficSourceItem[];
}

export interface TrafficRetentionMetrics {
  [time: string]: {
    count: number;
    percent: number;
  };
}

export interface TrafficRetentionItem {
  value: string;
  retention: TrafficRetentionMetrics;
}

export interface TrafficRetentionByType {
  utm_source: TrafficRetentionItem[];
  utm_medium: TrafficRetentionItem[];
  utm_campaign: TrafficRetentionItem[];
  utm_term: TrafficRetentionItem[];
  utm_content: TrafficRetentionItem[];
}

export interface HourlyPrimeTimeData {
  hour: number;
  unique_views: number;
  unique_plays: number;
  total_views: number;
  total_plays: number;
}

export interface DayOfWeekPrimeTimeData {
  day: number;
  day_name: string;
  unique_views: number;
  unique_plays: number;
  total_views: number;
  total_plays: number;
}

export interface HourlyByDayData {
  day: number;
  hour: number;
  unique_views: number;
  unique_plays: number;
  total_views: number;
  total_plays: number;
  device_dominant: string;
  device_percentage: number;
  conversions: number;
  revenue: number;
}

export interface PrimeTimesData {
  hourly: HourlyPrimeTimeData[];
  dayOfWeek: DayOfWeekPrimeTimeData[];
  hourlyByDay: HourlyByDayData[];
  peakHour: {
    hour: number;
    unique_views: number;
    unique_plays: number;
  };
  peakDay: {
    day: number;
    day_name: string;
    unique_views: number;
    unique_plays: number;
  };
}

export interface ConversionPlatform {
  name: string;
  logo: string;
  conversions: number;
  revenue: number;
}

export interface ConversionsData {
  total: number;
  revenue: number;
  platforms: ConversionPlatform[];
}

export interface MetricsSummary {
  views_total: number;
  views_unique: number;
  plays_total: number;
  plays_unique: number;
  play_rate: number;
  cta_clicks: number;
  engagement: number;
  /** Um item por pitch, na ordem de `video.pitches` (vazio = vídeo sem pitch). */
  pitches: SummaryPitch[];
  conversions: number;
  conversion_rate: number;
  revenue: number;
  currency: string;
}

// Defaults estáveis — evitam recriar objeto/array a cada render quando não há dados.
const LISTA_VAZIA: never[] = [];

const TRAFEGO_VAZIO: TrafficSourcesByType = {
  utm_source: [],
  utm_medium: [],
  utm_campaign: [],
  utm_term: [],
  utm_content: []
};

const RETENCAO_TRAFEGO_VAZIA: TrafficRetentionByType = {
  utm_source: [],
  utm_medium: [],
  utm_campaign: [],
  utm_term: [],
  utm_content: []
};

/**
 * Métricas consolidadas do vídeo.
 *
 * O fetch roda dentro do TanStack Query com a chave [videoId, inicio, fim]. Isso é
 * essencial porque `Layout.tsx` renderiza `{children}` DUAS vezes (bloco mobile
 * `lg:hidden` + bloco desktop `hidden lg:block`, alternados só por CSS), então o
 * MetricsDashboard existe em duas instâncias montadas ao mesmo tempo. Com o estado
 * local anterior (useState + useEffect) cada instância disparava seu próprio
 * GET /metric/consolidated. Compartilhando a mesma queryKey, as duas leem a MESMA
 * requisição e a rota é chamada uma única vez.
 */
export function useMetricsDataEnhanced(date?: DateRange | undefined, videoId?: string | null) {
  useLanguage();
  // A chave usa as datas já formatadas: o objeto DateRange tem identidade instável,
  // mas `yyyy-MM-dd` é o que de fato vai para a query string da API.
  const inicio = date?.from ? format(date.from, 'yyyy-MM-dd') : null;
  const fim = date?.to ? format(date.to, 'yyyy-MM-dd') : null;
  const habilitado = Boolean(videoId && inicio && fim);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['metrics', 'consolidated', videoId, inicio, fim],
    enabled: habilitado,
    // Mantém o resultado quente enquanto o usuário troca de aba no dashboard.
    staleTime: 60_000,
    // Sem retry e sem refetch no foco: preserva o comportamento antigo de uma
    // requisição por seleção e evita chamadas extras à rota.
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // Guarda explícita: o `enabled` já impede este caminho sem vídeo/datas,
      // mas assim o estreitamento de tipo não depende só dele.
      if (!videoId || !date?.from || !date?.to) return null;

      try {
        const response = await metricsService.getConsolidatedMetrics(
          videoId,
          date.from,
          date.to
        );

        if (!response?.success || !response?.data) return null;

        return response.data as any;
      } catch (error) {
        console.error('Erro ao buscar métricas:', error);
        toast.error(translateInterfaceText("Erro ao carregar métricas do vídeo"));
        throw error;
      }
    }
  });

  // Mesma superfície de retorno de antes — os consumidores (TabContent, abas,
  // FunnelTabContent) não precisam mudar.
  const metricas = useMemo(() => {
    const d = data;

    return {
      summary: (d?.summary ?? null) as MetricsSummary | null,
      viewsSeries: (d?.series?.views ?? LISTA_VAZIA) as ViewsSeries[],
      retentionTimeline: (d?.retention?.timeline ?? LISTA_VAZIA) as RetentionTimeline[],
      countries: (d?.geo?.countries ?? LISTA_VAZIA) as GeoData[],
      states: (d?.geo?.states ?? LISTA_VAZIA) as GeoData[],
      cities: (d?.geo?.cities ?? LISTA_VAZIA) as GeoData[],
      platforms: (d?.devices?.platforms ?? LISTA_VAZIA) as DeviceData[],
      browsers: (d?.devices?.browsers ?? LISTA_VAZIA) as DeviceData[],
      references: (d?.references ?? LISTA_VAZIA) as ReferenceData[],
      trafficSources: (d?.traffic ?? TRAFEGO_VAZIO) as TrafficSourcesByType,
      trafficRetention: (d?.trafficRetention ?? RETENCAO_TRAFEGO_VAZIA) as TrafficRetentionByType,
      primeTimes: (d?.primeTimes ?? null) as PrimeTimesData | null,
      conversions: (d?.conversions ?? null) as ConversionsData | null,
      videoInfo: (d?.video ?? null) as {
        id: string;
        duration: number;
        pitches: VideoPitch[];
        video_mp4?: string | null;
      } | null
    };
  }, [data]);

  // Devolve a promessa: os modais de pitch fazem `await onSaved()` antes de fechar, e
  // o save seguinte (PUT da lista inteira) precisa partir da lista já recarregada —
  // senão ele apaga o pitch que acabou de ser salvo.
  const refetchMetrics = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    ...metricas,
    isLoading,
    refetchMetrics
  };
}
